/**
 * batch-grant-bonus-execution — 批量 BONUS 调用既有单发端点时的有限并发与保守结果分类。
 *
 * grant-bonus 没有请求级重试幂等性；本编排只执行一次，且把非 401/422 失败视为可能已到账。
 */

import { AdminApiError, UnexpectedAdminApiResponseError } from '~/api/errors';

export const BATCH_GRANT_BONUS_MAX_CONCURRENCY = 5;

export type BatchBonusGrantOutcome<T> =
  | { recipient: T; status: 'succeeded' }
  | { recipient: T; status: 'safe-to-retry'; error: AdminApiError }
  | { recipient: T; status: 'ambiguous'; error: unknown };

export async function executeBatchBonusGrants<T>(
  recipients: readonly T[],
  grantOne: (recipient: T) => Promise<unknown>,
): Promise<Array<BatchBonusGrantOutcome<T>>> {
  const outcomes = new Array<BatchBonusGrantOutcome<T>>(recipients.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < recipients.length) {
      const index = nextIndex;
      nextIndex += 1;
      const recipient = recipients[index];
      if (recipient === undefined) continue;
      try {
        await grantOne(recipient);
        outcomes[index] = { recipient, status: 'succeeded' };
      } catch (error) {
        outcomes[index] =
          error instanceof AdminApiError &&
          !(error instanceof UnexpectedAdminApiResponseError) &&
          (error.httpStatus === 401 || error.httpStatus === 422)
            ? { recipient, status: 'safe-to-retry', error }
            : { recipient, status: 'ambiguous', error };
      }
    }
  }

  const workerCount = Math.min(BATCH_GRANT_BONUS_MAX_CONCURRENCY, recipients.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return outcomes;
}
